import { safeParseFloat } from '@/lib/utils'
import { PortfolioData } from '@/types/generated/common/account'

interface BalanceTickerData {
  coinPrice: number
  todayPnl: number
}

const USD_STABLE_CURRENCIES = new Set([
  'USD',
  'USDC',
  'USDT',
  'BUSD',
  'DAI',
  'FDUSD',
  'TUSD',
  'PYUSD',
  'USDE',
  'USDD',
  'USDS',
])

const USD_STABLE_TICKER_DATA: BalanceTickerData = {
  coinPrice: 1,
  todayPnl: 0,
}

export function isUsdStableCurrency(currency?: string | null) {
  return USD_STABLE_CURRENCIES.has(currency?.toUpperCase() ?? '')
}

/**
 * `tickerKey` and `currency` are deliberately separate: `tickerKey` is whatever
 * the caller's ticker cache is actually keyed by (e.g. `${exchange}:${symbol}`
 * for a cache that must not collide the same currency held on two different
 * venues — see use-balance-rows.ts), while the stablecoin fallback below must
 * always check the real bare currency, or a composite key like "2:USDC_USD"
 * fails `isUsdStableCurrency` and silently drops the $1 fallback.
 */
export function getBalanceTickerData(
  tickerDataMap: Map<string, BalanceTickerData>,
  tickerKey: string,
  currency: string
) {
  const tickerData = tickerDataMap.get(tickerKey)
  if (tickerData?.coinPrice) return tickerData
  if (isUsdStableCurrency(currency)) return USD_STABLE_TICKER_DATA
  return tickerData
}

/**
 * Whether a balance row's live ticker price can be read as USD.
 *
 * `buildSpotPairMap` only ever resolves USDC/USDT-quoted pairs, so a resolved
 * symbol is itself the guarantee. A row with no resolved pair falls back to
 * subscribing the bare currency: on a crypto venue that matches no instrument
 * and no ticker ever arrives (harmless), but on KRX the bare currency IS the
 * instrument — "005930" — and its ticker is quoted in won.
 *
 * USD-stable currencies are their own answer: `getBalanceTickerData` hands them
 * a synthetic price of 1 whether or not a ticker exists.
 */
export function isUsdQuotedTicker(currency: string, resolvedSymbol: string | null | undefined) {
  return Boolean(resolvedSymbol) || isUsdStableCurrency(currency)
}

/**
 * Prefers a live ticker price when we have one — it's more current than the
 * venue's own periodically-polled USD value (`totalEquityUsd`, e.g. Derive's
 * `mark_value`, refreshed every 30s). Falls back to the venue-reported value
 * ONLY for assets that have no tradeable spot pair to subscribe a ticker for
 * in the first place (`hasResolvedSpotPair: false` — Derive's fee-free spot
 * on/off-ramp pairs aren't continuously quoted, so a ticker-only price would
 * permanently read 0 for them even though the venue already told us what the
 * position is worth).
 *
 * When a spot pair DOES exist, a ticker is expected to arrive shortly — the
 * fallback is skipped in that case rather than raced against it. Dividing
 * `totalEquityUsd` by a near-dust `amount` before the real ticker lands can
 * produce a wildly inflated implied price (seen for a ~$0.09 ETH dust
 * balance rendering as a >$400M mark before its ticker arrived); blank
 * (`coinPrice: 0`, rendered as '-' by the caller) is safer than that.
 *
 * `tickerPriceIsUsd` (see [`isUsdQuotedTicker`]) is what keeps that preference
 * honest. Every output here is USD, and a ticker in some other quote currency
 * cannot be one: pricing a ₩249,500 KRX share off its own ticker reported
 * ~$249,500 of delta, ~1,370x high. Such a row has no USD ticker coming either,
 * so it takes the venue fallback rather than the blank-until-ticker path — for
 * it `totalEquityUsd` is not a stale second choice but the only right answer,
 * since exchange-toss already did the FX hop.
 */
export function resolveBalancePriceAndEquity(
  amount: number,
  totalEquityUsd: string | undefined,
  tickerCoinPrice: number | undefined,
  hasResolvedSpotPair: boolean,
  tickerPriceIsUsd: boolean
): { coinPrice: number; equityValue: number } {
  if (tickerCoinPrice && tickerPriceIsUsd) {
    return { coinPrice: tickerCoinPrice, equityValue: amount * tickerCoinPrice }
  }
  if (amount === 0 || (hasResolvedSpotPair && tickerPriceIsUsd)) {
    return { coinPrice: 0, equityValue: 0 }
  }
  const venueEquityUsd = totalEquityUsd ? safeParseFloat(totalEquityUsd) : 0
  return { coinPrice: venueEquityUsd / amount, equityValue: venueEquityUsd }
}

/**
 * Merges balance rows for the same currency WITHIN THE SAME ACCOUNT, summing
 * `equity` and `totalEquityUsd` together — a venue can report several components
 * under one currency (see `PortfolioData.totalEquityUsd`'s doc comment), and
 * keeping only one component's totalEquityUsd against the summed equity would
 * mis-price the row in `resolveBalancePriceAndEquity`. Keyed by accountId+currency
 * (not currency alone) so aggregating balances across multiple accounts never
 * collapses two different accounts' same-currency holdings into one row.
 */
export function mergeSameCurrencyBalances(balances: PortfolioData[]): PortfolioData[] {
  const map = new Map<string, PortfolioData>()
  balances.forEach((bal) => {
    // 'USD' is a synthetic fiat aggregate, never a wallet asset. USDT IS a real
    // wallet asset (Bybit UNIFIED collateral) and must be shown.
    if (bal.currency === 'USD') return
    const equity = safeParseFloat(bal.equity)
    if (equity === 0) return
    const key = `${bal.accountId}:${bal.currency}`
    const existing = map.get(key)
    if (!existing) {
      map.set(key, { ...bal })
      return
    }
    const totalEquityUsd =
      existing.totalEquityUsd || bal.totalEquityUsd
        ? (safeParseFloat(existing.totalEquityUsd) + safeParseFloat(bal.totalEquityUsd)).toString()
        : undefined
    map.set(key, {
      ...existing,
      equity: (safeParseFloat(existing.equity) + equity).toString(),
      totalEquityUsd,
    })
  })
  return Array.from(map.values())
}
